import { useRef, useState } from 'react';
import { settingsApi } from '../../api';
import { useLoader } from '../../components/common';
import { IconPrinter } from '../../components/icons';
import { SettingsPage, Panel } from './shell';

const bool = (value) => value === true || value === 'true';
const money = (value) => Number(value || 0).toLocaleString('th-TH', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalize = (settings = {}) => ({
  nameTh: settings.nameTh || '',
  addressTh: settings.addressTh || '',
  nameEn: settings.nameEn || '',
  addressEn: settings.addressEn || '',
  phone: settings.phone || '',
  taxId: settings.taxId || '',
  logoUrl: settings.logoUrl || '',
  docType: settings.docType || 'receipt',
  language: settings.language || 'th',
  itemGrouping: settings.itemGrouping || 'by_item',
  showCustomerName: settings.showCustomerName === undefined
    ? true
    : bool(settings.showCustomerName),
  customerDetails: settings.customerDetails || 'none',
  showInvoiceCheckbox: bool(settings.showInvoiceCheckbox),
  showChange: bool(settings.showChange),
  showPosId: bool(settings.showPosId),
  showSignature: bool(settings.showSignature),
});

function SettingRow({ label, children, top = false }) {
  return (
    <div className={`receipt-setting-row${top ? ' top' : ''}`}>
      <div className="receipt-setting-label">{label}</div>
      <div className="receipt-setting-control">{children}</div>
    </div>
  );
}

function ChoiceRow({ label, value, options, onChange, disabled }) {
  return (
    <SettingRow label={label}>
      <div className="receipt-choice-group">
        {options.map((option) => (
          <label
            key={String(option.value)}
            className={`receipt-radio${value === option.value ? ' selected' : ''}${disabled ? ' disabled' : ''}`}
          >
            <input
              type="radio"
              checked={value === option.value}
              disabled={disabled}
              onChange={() => onChange(option.value)}
            />
            <span className="receipt-radio-mark" />
            {option.label}
          </label>
        ))}
      </div>
    </SettingRow>
  );
}

function ToggleRow({ label, checked, onChange, disabled }) {
  return (
    <SettingRow label={label}>
      <label className={`receipt-check${disabled ? ' disabled' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="receipt-check-box">✓</span>
        <span>{checked ? 'แสดง' : 'ไม่แสดง'}</span>
      </label>
    </SettingRow>
  );
}

function ReceiptPreview({ settings, previewMode, onPreviewMode, printRef, onPrint }) {
  const english = settings.language === 'en';
  const shopName = english
    ? settings.nameEn || settings.nameTh || 'SAANTI MASSAGE'
    : settings.nameTh || settings.nameEn || 'SAANTI MASSAGE';
  const address = english
    ? settings.addressEn || settings.addressTh
    : settings.addressTh || settings.addressEn;
  const documentTitle = settings.docType === 'abbreviated_tax'
    ? (english ? 'Abbreviated Tax Invoice' : 'ใบกำกับภาษีอย่างย่อ')
    : (english ? 'Receipt' : 'ใบเสร็จรับเงิน');
  const showCustomer = settings.customerDetails !== 'none';
  const customerName = settings.showCustomerName ? 'คุณแอน (Ann)' : 'Ann Saanti';
  const items = previewMode === 'by_customer'
    ? [
      { qty: 1, name: english ? 'Customer 1 · Thai Massage 60 min' : 'ลูกค้าคนที่ 1 · นวดไทย 60 นาที', amount: 600 },
      { qty: 1, name: english ? 'Customer 2 · Foot Massage 60 min' : 'ลูกค้าคนที่ 2 · นวดเท้า 60 นาที', amount: 600 },
    ]
    : [
      { qty: 1, name: english ? 'Thai Massage 60 min' : 'นวดไทย 60 นาที', amount: 600 },
      { qty: 1, name: english ? 'Foot Massage 60 min' : 'นวดเท้า 60 นาที', amount: 600 },
    ];
  const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
  const discount = 100;
  const net = subtotal - discount;
  const itemCount = items.reduce((sum, item) => sum + item.qty, 0);
  const logoUrl = settings.logoUrl || '/spa-images/saanti-logo-gold-transparent.png';

  return (
    <aside className="receipt-preview-column">
      <div className="receipt-preview-tabs" aria-label="รูปแบบตัวอย่าง">
        <button
          type="button"
          className={previewMode === 'by_item' ? 'active' : ''}
          onClick={() => onPreviewMode('by_item')}
        >
          ตามรายการ
        </button>
        <button
          type="button"
          className={previewMode === 'by_customer' ? 'active' : ''}
          onClick={() => onPreviewMode('by_customer')}
        >
          ตามลูกค้า
        </button>
      </div>

      <div className="receipt-preview-title">ตัวอย่างใบเสร็จ</div>
      <div className="receipt-paper" ref={printRef}>
        <div className="receipt-paper-head">
          <img src={logoUrl} alt="โลโก้ร้าน" />
          <div className="receipt-shop-name">{shopName}</div>
          {address && <div>{address}</div>}
          {settings.phone && <div>{english ? 'Tel.' : 'โทร'} {settings.phone}</div>}
          {settings.taxId && <div>{english ? 'Tax ID' : 'เลขประจำตัวผู้เสียภาษี'} {settings.taxId}</div>}
          <div className="receipt-tagline">Thai massage</div>
        </div>

        <div className="receipt-heavy-rule" />
        <div className="receipt-meta">
          <div className="receipt-id-line">
            <span>{documentTitle} :</span><b>R0123456789</b>
          </div>
          <div><span>Order No. :</span><b>12345-678</b></div>
          <div>
            <span>{english ? 'Booking time :' : 'เวลาจอง :'}</span>
            <b>30/07/2026 (10:00-11:00)</b>
          </div>
          <div className="receipt-meta-split">
            <span>{english ? 'Date :' : 'วันที่ :'} <b>30/07/2026 (17:03)</b></span>
            <span>{english ? 'Cashier :' : 'แคชเชียร์ :'} <b>manassy</b></span>
          </div>
          {settings.showPosId && <div><span>POS ID :</span><b>POS-01</b></div>}
        </div>

        {showCustomer && (
          <div className="receipt-customer">
            <div><span>{english ? 'Name:' : 'ชื่อ:'}</span><b>{customerName}</b></div>
            <div><span>{english ? 'Phone:' : 'เบอร์โทรศัพท์:'}</span><b>0812345678</b></div>
            {settings.customerDetails === 'gender' && (
              <div><span>{english ? 'Gender:' : 'เพศ:'}</span><b>{english ? 'Female' : 'หญิง'}</b></div>
            )}
          </div>
        )}

        <table className="receipt-items">
          <thead>
            <tr>
              <th>{english ? 'Qty' : 'จำนวน'}</th>
              <th>{english ? 'Item' : 'รายการ'}</th>
              <th>{english ? 'Amount' : 'จำนวนเงิน (บาท)'}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.name}>
                <td>{item.qty}</td>
                <td>{item.name}</td>
                <td>{money(item.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="receipt-item-count">
          {english ? `Total ${itemCount} items` : `รวม ${itemCount} รายการ`}
        </div>
        <div className="receipt-totals">
          <div><span>{english ? 'Subtotal' : 'ยอดก่อนส่วนลด'}</span><b>{money(subtotal)}</b></div>
          <div><span>{english ? 'Discount' : 'ส่วนลด'}</span><b>- {money(discount)}</b></div>
          <div><span>{english ? 'After discount' : 'ยอดหลังหักส่วนลด'}</span><b>{money(net)}</b></div>
          <div className="receipt-payment-row"><span>{english ? 'Payment' : 'ชำระโดย'}</span><b>{english ? 'Cash' : 'เงินสด'}</b></div>
          <div className="grand"><span>{english ? 'Amount due' : 'ยอดชำระ'}</span><b>{money(net)}</b></div>
          {settings.showChange && <div><span>{english ? 'Change' : 'เงินทอน'}</span><b>0.00</b></div>}
        </div>

        {settings.showInvoiceCheckbox && (
          <div className="receipt-invoice-option">□ {english ? 'Request a full tax invoice' : 'ต้องการใบกำกับภาษีเต็มรูปแบบ'}</div>
        )}
        {settings.showSignature && (
          <div className="receipt-signature">
            <span>................................................</span>
            <small>{english ? 'Authorized signature' : 'ลายเซ็นผู้รับเงิน'}</small>
          </div>
        )}
        <div className="receipt-thanks">{english ? 'Thank you' : 'ขอบคุณที่ใช้บริการ'}</div>
      </div>

      <button type="button" className="receipt-print-button" onClick={onPrint}>
        <IconPrinter size={17} /> พิมพ์ใบเสร็จ
      </button>
    </aside>
  );
}

export function SettingBill() {
  const receipt = useLoader(() => settingsApi.receipt(), []);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewMode, setPreviewMode] = useState(null);
  const fileRef = useRef(null);
  const printRef = useRef(null);

  const update = (key, value, source) => setDraft({ ...source, [key]: value });

  const uploadLogo = (event, source) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('กรุณาเลือกไฟล์รูปภาพ');
      return;
    }
    if (file.size > 1_200_000) {
      alert('รูปโลโก้ต้องมีขนาดไม่เกิน 1.2 MB');
      return;
    }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = () => {
      update('logoUrl', String(reader.result || ''), source);
      setUploading(false);
    };
    reader.onerror = () => {
      alert('ไม่สามารถอ่านไฟล์รูปได้');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await settingsApi.updateReceipt(draft);
      await receipt.reload();
      setDraft(null);
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const printReceipt = () => {
    const content = printRef.current?.outerHTML;
    if (!content) return;
    const popup = window.open('', 'saanti-receipt-print', 'width=520,height=760');
    if (!popup) {
      alert('เบราว์เซอร์บล็อกหน้าต่างพิมพ์ กรุณาอนุญาต pop-up แล้วลองอีกครั้ง');
      return;
    }
    popup.document.write(`<!doctype html>
      <html><head><title>Receipt</title><meta charset="utf-8">
      <base href="${window.location.origin}/">
      <style>
        *{box-sizing:border-box}
        body{margin:0;padding:5mm;color:#111;background:#fff;font-family:Arial,"Noto Sans Thai",sans-serif}
        .receipt-paper{width:100%;font-size:3.15mm;line-height:1.45}
        .receipt-paper-head{text-align:center;line-height:1.55}
        .receipt-paper-head img{display:block;width:40mm;height:34mm;object-fit:contain;margin:0 auto 2mm;filter:grayscale(1) contrast(1.35)}
        .receipt-shop-name{font-size:3.4mm;font-weight:600}
        .receipt-paper-head>div:not(.receipt-shop-name):not(.receipt-tagline){color:#555;font-size:2.5mm}
        .receipt-tagline{margin:4mm 0 3.5mm;color:#777;font-size:2.1mm;letter-spacing:.42em}
        .receipt-heavy-rule{height:.8mm;margin-bottom:2mm;background:#111}
        .receipt-meta{padding:0 0 2mm;border-bottom:.55mm dashed #111}
        .receipt-meta>div{display:flex;align-items:baseline;gap:1.3mm;padding:.8mm 0}
        .receipt-meta b{font-weight:500}.receipt-meta .receipt-id-line{font-size:4.2mm}
        .receipt-meta-split{justify-content:space-between;gap:4mm!important}
        .receipt-meta-split>span:last-child{margin-left:auto;text-align:right}
        .receipt-customer{padding:2mm 0}.receipt-customer>div{display:flex;gap:1.3mm;padding:.55mm 0}
        .receipt-customer b{font-weight:500}
        table{width:100%;margin:0;border-top:.55mm solid #111;border-bottom:.55mm dashed #111;border-collapse:collapse;table-layout:fixed}
        th,td{padding:1.3mm .5mm;border:0;vertical-align:top;font-size:3mm;text-align:left}
        th{padding-bottom:.5mm;font-weight:500}td{padding-top:.5mm}
        th:first-child,td:first-child{width:15mm;text-align:left}th:last-child,td:last-child{width:27mm;text-align:right}
        .receipt-item-count{padding:2mm 0 1.2mm}
        .receipt-totals{width:100%;padding-bottom:2mm;border-bottom:.55mm solid #111}
        .receipt-totals>div{display:flex;justify-content:space-between;align-items:baseline;gap:4mm;padding:.8mm 0}
        .receipt-totals b{min-width:24mm;font-weight:500;text-align:right}
        .receipt-payment-row{margin-top:.8mm;border-top:.3mm solid #bbb}
        .receipt-totals .grand{margin-top:1.3mm;padding:1.8mm 0 1mm;font-size:4.8mm;font-weight:700}
        .receipt-totals .grand span{font-weight:700}
        .receipt-totals .grand b{border-bottom:1mm double #111;font-size:5.2mm;font-weight:700;line-height:1.05}
        .receipt-invoice-option{margin-top:3mm}.receipt-signature{text-align:center;margin:10mm 0 3mm}
        .receipt-signature span,.receipt-signature small{display:block}.receipt-thanks{text-align:center;margin-top:5mm;color:#555;font-size:2.8mm}
        @page{size:80mm auto;margin:4mm}
        @media print{body{padding:0}}
      </style></head><body>${content}</body></html>`);
    popup.document.close();
    const runPrint = () => {
      popup.focus();
      popup.print();
      popup.close();
    };
    const logo = popup.document.querySelector('.receipt-paper-head img');
    if (logo && !logo.complete) {
      logo.addEventListener('load', runPrint, { once: true });
      logo.addEventListener('error', runPrint, { once: true });
    } else {
      window.setTimeout(runPrint, 80);
    }
  };

  return (
    <SettingsPage>
      <Panel loader={receipt}>
        {(data) => {
          const saved = normalize(data.settings);
          const values = draft || saved;
          const editing = Boolean(draft);
          const change = (key, value) => update(key, value, values);

          return (
            <div className="receipt-generator">
              <section className="receipt-settings-column">
                <h3>ข้อมูลใบเสร็จ</h3>

                <SettingRow label="ชื่อ (ภาษาไทย)" top>
                  <textarea
                    value={values.nameTh}
                    disabled={!editing}
                    onChange={(event) => change('nameTh', event.target.value)}
                  />
                </SettingRow>
                <SettingRow label="ที่อยู่ (ภาษาไทย)" top>
                  <textarea
                    value={values.addressTh}
                    disabled={!editing}
                    onChange={(event) => change('addressTh', event.target.value)}
                  />
                </SettingRow>
                <SettingRow label="ชื่อ (ภาษาอังกฤษ)" top>
                  <textarea
                    value={values.nameEn}
                    disabled={!editing}
                    onChange={(event) => change('nameEn', event.target.value)}
                  />
                </SettingRow>
                <SettingRow label="ที่อยู่ (ภาษาอังกฤษ)" top>
                  <textarea
                    value={values.addressEn}
                    disabled={!editing}
                    onChange={(event) => change('addressEn', event.target.value)}
                  />
                </SettingRow>
                <SettingRow label="เบอร์โทรศัพท์">
                  <input
                    value={values.phone}
                    disabled={!editing}
                    onChange={(event) => change('phone', event.target.value)}
                  />
                </SettingRow>
                <SettingRow label="เลขประจำตัวผู้เสียภาษี">
                  <input
                    value={values.taxId}
                    disabled={!editing}
                    onChange={(event) => change('taxId', event.target.value)}
                  />
                </SettingRow>

                <SettingRow label="โลโก้" top>
                  <div className="receipt-logo-control">
                    <div className="receipt-logo-box">
                      {values.logoUrl
                        ? <img src={values.logoUrl} alt="โลโก้ร้าน" />
                        : <span>ไม่มีรูป</span>}
                    </div>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => uploadLogo(event, values)}
                    />
                    <div className="receipt-logo-actions">
                      <button
                        type="button"
                        disabled={!editing || uploading}
                        onClick={() => fileRef.current?.click()}
                      >
                        {uploading ? 'กำลังอ่านรูป…' : 'อัพโหลดรูป'}
                      </button>
                      <button
                        type="button"
                        className="danger"
                        disabled={!editing || !values.logoUrl}
                        onClick={() => change('logoUrl', '')}
                      >
                        ลบรูป
                      </button>
                      <small>รองรับ JPG, PNG หรือ WEBP ขนาดไม่เกิน 1.2 MB</small>
                    </div>
                  </div>
                </SettingRow>

                <div className="receipt-settings-divider" />

                <ChoiceRow
                  label="ตั้งค่าประเภทใบเสร็จ"
                  value={values.docType}
                  disabled={!editing}
                  onChange={(value) => change('docType', value)}
                  options={[
                    { value: 'receipt', label: 'ใบเสร็จรับเงิน' },
                    { value: 'abbreviated_tax', label: 'ใบกำกับภาษีแบบย่อ' },
                  ]}
                />
                <ChoiceRow
                  label="ตั้งค่าภาษาเริ่มต้น"
                  value={values.language}
                  disabled={!editing}
                  onChange={(value) => change('language', value)}
                  options={[
                    { value: 'th', label: 'ภาษาไทย' },
                    { value: 'en', label: 'ภาษาอังกฤษ' },
                  ]}
                />
                <ChoiceRow
                  label="รายการบริการ"
                  value={values.itemGrouping}
                  disabled={!editing}
                  onChange={(value) => {
                    change('itemGrouping', value);
                    setPreviewMode(value);
                  }}
                  options={[
                    { value: 'by_item', label: 'ตามรายการ' },
                    { value: 'by_customer', label: 'ตามลูกค้า' },
                  ]}
                />
                <ChoiceRow
                  label="แสดงชื่อลูกค้า"
                  value={values.showCustomerName}
                  disabled={!editing}
                  onChange={(value) => change('showCustomerName', value)}
                  options={[
                    { value: true, label: 'แสดงชื่อเล่น' },
                    { value: false, label: 'แสดงเฉพาะชื่อจริง' },
                  ]}
                />
                <ChoiceRow
                  label="ข้อมูลลูกค้ารายคน"
                  value={values.customerDetails}
                  disabled={!editing}
                  onChange={(value) => change('customerDetails', value)}
                  options={[
                    { value: 'none', label: 'ไม่แสดงข้อมูลลูกค้า' },
                    { value: 'name', label: 'แสดงชื่อลูกค้า' },
                    { value: 'gender', label: 'แสดงเพศลูกค้า' },
                  ]}
                />
                <ChoiceRow
                  label="ใบแจ้งหนี้แยกตามบิล"
                  value={values.showInvoiceCheckbox}
                  disabled={!editing}
                  onChange={(value) => change('showInvoiceCheckbox', value)}
                  options={[
                    { value: false, label: 'ไม่แสดงช่องทำเครื่องหมาย' },
                    { value: true, label: 'แสดงช่องทำเครื่องหมาย' },
                  ]}
                />
                <ToggleRow
                  label="แสดงเงินทอน"
                  checked={values.showChange}
                  disabled={!editing}
                  onChange={(value) => change('showChange', value)}
                />
                <ToggleRow
                  label="แสดง POS ID"
                  checked={values.showPosId}
                  disabled={!editing}
                  onChange={(value) => change('showPosId', value)}
                />
                <ToggleRow
                  label="แสดงลายเซ็น"
                  checked={values.showSignature}
                  disabled={!editing}
                  onChange={(value) => change('showSignature', value)}
                />
              </section>

              <ReceiptPreview
                settings={values}
                previewMode={previewMode || values.itemGrouping}
                onPreviewMode={setPreviewMode}
                printRef={printRef}
                onPrint={printReceipt}
              />

              <div className="receipt-form-actions">
                {editing ? (
                  <>
                    <button
                      type="button"
                      className="receipt-cancel-button"
                      disabled={saving}
                      onClick={() => setDraft(null)}
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="button"
                      className="btn-solid receipt-save-button"
                      disabled={saving || uploading}
                      onClick={save}
                    >
                      {saving ? 'กำลังบันทึก…' : 'บันทึก'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn-solid receipt-edit-button"
                    onClick={() => {
                      setDraft(saved);
                      setPreviewMode(saved.itemGrouping);
                    }}
                  >
                    แก้ไข
                  </button>
                )}
              </div>
            </div>
          );
        }}
      </Panel>
    </SettingsPage>
  );
}
